import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BrandExtraction } from "../components/brand/BrandExtraction";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Card } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { useBrand } from "../hooks/useBrand";
import { useStartPipeline } from "../hooks/useContent";
import { useCreditBalance, useSubscribe } from "../hooks/useCredits";

const TOTAL_STEPS = 4;

// ── Progress Indicator ──

function ProgressDots({
  current,
  total,
}: {
  current: number;
  total: number;
}) {
  return (
    <div className="flex items-center justify-center gap-3">
      {Array.from({ length: total }).map((_, i) => {
        const step = i + 1;
        const isCompleted = step < current;
        const isActive = step === current;

        return (
          <span
            key={step}
            className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors ${
              isCompleted
                ? "bg-brand-600 text-white"
                : isActive
                  ? "bg-brand-600 text-white"
                  : "border-2 border-slate-300 text-slate-400"
            }`}
            aria-label={
              isCompleted
                ? `Step ${step} completed`
                : isActive
                  ? `Step ${step} active`
                  : `Step ${step} upcoming`
            }
          >
            {isCompleted ? (
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            ) : (
              step
            )}
          </span>
        );
      })}
    </div>
  );
}

// ── Step 1: Brand Setup ──

function BrandSetupStep({
  onComplete,
  onSkip,
}: {
  onComplete: (brandId: string) => void;
  onSkip: () => void;
}) {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-slate-900">
          Let's set up your brand
        </h2>
        <p className="mt-2 text-sm text-slate-500">
          Paste your website URL and we'll extract your brand identity
          automatically.
        </p>
      </div>
      <BrandExtraction onComplete={onComplete} />
      <div className="text-center">
        <button
          type="button"
          onClick={onSkip}
          className="text-sm text-slate-400 hover:text-slate-600"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}

// ── Step 2: Review Brand ──

function ReviewBrandStep({
  brandId,
  onNext,
  onBack,
}: {
  brandId: string | null;
  onNext: () => void;
  onBack: () => void;
}) {
  const { data: brandData, isLoading } = useBrand(brandId ?? undefined);
  const brand = brandData?.data;

  const [name, setName] = useState("");
  const [industry, setIndustry] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [initialized, setInitialized] = useState(false);

  // Populate fields once brand data loads
  if (brand && !initialized) {
    setName(brand.name ?? "");
    setIndustry(brand.industry ?? "");
    setTargetAudience(brand.target_audience ?? "");
    setInitialized(true);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (!brand) {
    return (
      <div className="space-y-6 text-center">
        <h2 className="text-2xl font-bold text-slate-900">
          Review your brand profile
        </h2>
        <p className="text-sm text-slate-500">
          No brand found. You can set one up later from the Brand page.
        </p>
        <div className="flex justify-center gap-3">
          <Button variant="secondary" onClick={onBack}>
            Back
          </Button>
          <Button onClick={onNext}>Continue</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-slate-900">
          Review your brand profile
        </h2>
        <p className="mt-2 text-sm text-slate-500">
          Make any adjustments to your brand details.
        </p>
      </div>

      <div className="space-y-4">
        <Input
          label="Brand Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your brand name"
        />
        <Input
          label="Industry"
          value={industry}
          onChange={(e) => setIndustry(e.target.value)}
          placeholder="e.g. Technology, Health, Education"
        />
        <Input
          label="Target Audience"
          value={targetAudience}
          onChange={(e) => setTargetAudience(e.target.value)}
          placeholder="Who are you trying to reach?"
        />
      </div>

      <div className="flex justify-center gap-3">
        <Button variant="secondary" onClick={onBack}>
          Back
        </Button>
        <Button onClick={onNext}>Looks good</Button>
      </div>
    </div>
  );
}

// ── Step 3: Connect Platforms ──

const PLATFORMS = [
  {
    id: "linkedin",
    name: "LinkedIn",
    description: "Professional network for B2B content",
    icon: (
      <svg
        className="h-8 w-8"
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  },
  {
    id: "twitter",
    name: "Twitter / X",
    description: "Short-form content and engagement",
    icon: (
      <svg
        className="h-8 w-8"
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
  {
    id: "instagram",
    name: "Instagram",
    description: "Visual storytelling and reels",
    icon: (
      <svg
        className="h-8 w-8"
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
      </svg>
    ),
  },
] as const;

function ConnectPlatformsStep({
  onNext,
  onSkip,
}: {
  onNext: () => void;
  onSkip: () => void;
}) {
  const [connected, setConnected] = useState<Record<string, boolean>>({});

  function handleConnect(platformId: string) {
    // Open platform connect in new tab (placeholder URL)
    window.open(`/api/auth/${platformId}/connect`, "_blank");
    // Optimistically mark as connected for demo
    setConnected((prev) => ({ ...prev, [platformId]: true }));
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-slate-900">
          Connect your platforms
        </h2>
        <p className="mt-2 text-sm text-slate-500">
          Connect the platforms where you want to publish content.
        </p>
      </div>

      <div className="space-y-3">
        {PLATFORMS.map((platform) => (
          <Card key={platform.id}>
            <div className="flex items-center gap-4">
              <span className="flex-shrink-0 text-slate-700">
                {platform.icon}
              </span>
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-900">
                  {platform.name}
                </p>
                <p className="text-xs text-slate-500">
                  {platform.description}
                </p>
              </div>
              {connected[platform.id] ? (
                <Badge variant="success">Connected</Badge>
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleConnect(platform.id)}
                >
                  Connect
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>

      <div className="flex flex-col items-center gap-2">
        <Button onClick={onNext}>Continue</Button>
        <button
          type="button"
          onClick={onSkip}
          className="text-sm text-slate-400 hover:text-slate-600"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}

// ── Upgrade Nudge (Sub-Task 13.3) ──

function UpgradeNudge({ onContinue }: { onContinue: () => void }) {
  const { data: creditData } = useCreditBalance();
  const subscribe = useSubscribe();
  const balance = creditData?.data;
  const credits = balance?.credits ?? 0;

  async function handleUpgrade() {
    try {
      const result = await subscribe.mutateAsync({
        success_url: `${window.location.origin}/onboarding?upgraded=true`,
        cancel_url: `${window.location.origin}/onboarding`,
      });
      const checkoutUrl = result.data?.checkout_url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      }
    } catch {
      // Error handled by mutation state
    }
  }

  // Only show if credits are low (3 or fewer)
  if (credits > 3) {
    onContinue();
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-slate-900">
          Ready to unlock more?
        </h2>
        <p className="mt-2 text-sm text-slate-500">
          You have{" "}
          <span className="font-semibold text-slate-700">{credits}</span> of 3
          free credits. Upgrade to Pro for $20/month and get 20 credits.
        </p>
      </div>

      <Card className="text-center">
        <p className="text-3xl font-bold text-slate-900">$20</p>
        <p className="text-sm text-slate-500">per month</p>
        <ul className="mt-4 space-y-2 text-left text-sm text-slate-600">
          <li className="flex items-center gap-2">
            <svg
              className="h-4 w-4 flex-shrink-0 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            20 content credits per month
          </li>
          <li className="flex items-center gap-2">
            <svg
              className="h-4 w-4 flex-shrink-0 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            All 6 AI agents
          </li>
          <li className="flex items-center gap-2">
            <svg
              className="h-4 w-4 flex-shrink-0 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            Multi-platform publishing
          </li>
        </ul>
        <div className="mt-6">
          <Button
            onClick={handleUpgrade}
            loading={subscribe.isPending}
            className="w-full"
          >
            Upgrade
          </Button>
        </div>
        {subscribe.isError && (
          <p className="mt-2 text-sm text-red-500">
            Failed to start checkout. Please try again.
          </p>
        )}
      </Card>

      <div className="text-center">
        <button
          type="button"
          onClick={onContinue}
          className="text-sm text-slate-400 hover:text-slate-600"
        >
          Continue with free plan
        </button>
      </div>
    </div>
  );
}

// ── Step 4: First Check-in ──

function FirstCheckinStep({
  brandId,
  onSkip,
}: {
  brandId: string | null;
  onSkip: () => void;
}) {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const startPipeline = useStartPipeline();

  async function handleGenerate() {
    if (!text.trim()) return;
    try {
      const result = await startPipeline.mutateAsync({
        brand_id: brandId ?? "",
        text: text.trim(),
      });
      const runId = result.data?.id;
      if (runId) {
        navigate(`/ideas/${runId}`);
      }
    } catch {
      // Error handled by mutation state
    }
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-slate-900">
          Try your first content run
        </h2>
        <p className="mt-2 text-sm text-slate-500">
          Tell us what you worked on this week and we'll generate content for
          you.
        </p>
      </div>

      <div className="space-y-3">
        <label
          htmlFor="checkin-text"
          className="text-sm font-medium text-slate-700"
        >
          What did you work on this week?
        </label>
        <textarea
          id="checkin-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="We launched a new feature that lets users..."
          rows={5}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
      </div>

      {startPipeline.isError && (
        <p className="text-sm text-red-500">
          Failed to generate content. Please try again.
        </p>
      )}

      <div className="flex flex-col items-center gap-2">
        <Button
          onClick={handleGenerate}
          disabled={!text.trim()}
          loading={startPipeline.isPending}
        >
          Generate Content
        </Button>
        <button
          type="button"
          onClick={onSkip}
          className="text-sm text-slate-400 hover:text-slate-600"
        >
          Skip to dashboard
        </button>
      </div>
    </div>
  );
}

// ── Main Onboarding Page ──

export function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [brandId, setBrandId] = useState<string | null>(null);
  const [showUpgrade, setShowUpgrade] = useState(false);

  function goToDashboard() {
    navigate("/dashboard");
  }

  function handleBrandComplete(id: string) {
    setBrandId(id);
    setStep(2);
  }

  function handleStep3Next() {
    // Show upgrade nudge before step 4
    setShowUpgrade(true);
  }

  function handleUpgradeContinue() {
    setShowUpgrade(false);
    setStep(4);
  }

  return (
    <div className="flex min-h-screen items-start justify-center bg-white px-4 py-12">
      <div className="w-full max-w-lg">
        {/* Progress Dots */}
        <div className="mb-10">
          <ProgressDots current={step} total={TOTAL_STEPS} />
        </div>

        {/* Step Content */}
        {step === 1 && (
          <BrandSetupStep
            onComplete={handleBrandComplete}
            onSkip={() => setStep(2)}
          />
        )}

        {step === 2 && (
          <ReviewBrandStep
            brandId={brandId}
            onNext={() => setStep(3)}
            onBack={() => setStep(1)}
          />
        )}

        {step === 3 && !showUpgrade && (
          <ConnectPlatformsStep
            onNext={handleStep3Next}
            onSkip={handleStep3Next}
          />
        )}

        {showUpgrade && (
          <UpgradeNudge onContinue={handleUpgradeContinue} />
        )}

        {step === 4 && !showUpgrade && (
          <FirstCheckinStep brandId={brandId} onSkip={goToDashboard} />
        )}
      </div>
    </div>
  );
}
